import { Box, Flex, Heading, Skeleton, useToast } from '@chakra-ui/react';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { useGetDailyConcentrationInsightsQuery } from '../../requests/requests.gql.generated';
import {
  getConcentrationLimitFormatsByKey,
  ConcentrationLimitFormatMap,
} from '../shared/portfolioChartUtils';
import { PortfolioCoreBorrowingBaseChartTemplate } from '../shared/PortfolioCoreBorrowingBaseChartTemplate';
import { PortfolioVisibilityGroupButton } from '../shared/PortfolioVisibilityGroupButton';
import {
  chartGroupMap,
  generateConcentrationLimitChartConfig,
} from './portfolioBorrowingBaseChartConfigs';
import { PortfolioEligibilityKickoutsChart } from './PortfolioBorrowingBaseEligibilityChart';
import { PortfolioConcentrationLimitsChartTemplate } from './PortfolioConcentrationLimitsChartTemplate';
import { PortfolioExcessConcentrationChart } from './PortfolioExcessConcentrationChart';
import { colors } from 'packages/common-frontend/colors';
import { HighlightBox } from 'packages/common-frontend/components/HighlightBox';
import {
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
  errorToastConfig,
} from 'packages/common-frontend/constants';
import { useQueryParams } from 'packages/common-frontend/hooks/useQueryParams';
import { creditFacilityIdQueryParam } from 'packages/credit-facility/ui/CreditFacilitySelect';

const borrowingBaseDisplayModes = [
  'all',
  'borrowingBase',
  'eligibility',
  'concentration',
] as const;
type BorrowingBaseChartDisplayMode = typeof borrowingBaseDisplayModes[number];
const isBorrowingBaseChartDisplayMode = (
  test: any,
): test is BorrowingBaseChartDisplayMode => {
  return (
    test &&
    typeof test === 'string' &&
    _.includes(borrowingBaseDisplayModes, test)
  );
};

const borrowingBaseDisplayModeQueryParam = 'display';

const { borrowingBase, eligibility } = chartGroupMap;

type PortfolioBorrowingBaseContainerProps = { creditFacilityId: string };
export const PortfolioBorrowingBaseContainer = ({
  creditFacilityId,
}: PortfolioBorrowingBaseContainerProps) => {
  const toast = useToast();
  const navigate = useNavigate();
  const query = useQueryParams();
  const queryDisplayMode = query.get(borrowingBaseDisplayModeQueryParam);
  const displayMode = isBorrowingBaseChartDisplayMode(queryDisplayMode)
    ? queryDisplayMode
    : 'all';

  const {
    data: concentrationData,
    loading: isConcentrationDataLoading,
  } = useGetDailyConcentrationInsightsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
  });

  const concentrationLimitFormatMap = useMemo<ConcentrationLimitFormatMap>(() => {
    if (!concentrationData) {
      return new Map();
    }
    return getConcentrationLimitFormatsByKey(
      concentrationData.getDailyConcentrationInsights,
    );
  }, [concentrationData]);

  // @dani portfolio update this skeleton design
  if (!concentrationData || isConcentrationDataLoading) {
    return <Skeleton h="700px" />;
  }

  const totalChartCounts = {
    borrowingBase: borrowingBase.length,
    eligibility: eligibility.length + 1, // 1 additional for the aggregate chart
    concentration: concentrationLimitFormatMap.size + 1, // 1 additional for the aggregate chart
  };

  const onSetDisplayMode = (
    selectedDisplayMode: BorrowingBaseChartDisplayMode,
  ) => {
    const covenantsBaseRoute = '/portfolio/borrowing-base';
    const queryParams = `?${creditFacilityIdQueryParam}=${creditFacilityId}&${borrowingBaseDisplayModeQueryParam}=${selectedDisplayMode}`;

    navigate(`${covenantsBaseRoute}${queryParams}`);
  };

  return (
    <Box>
      <Flex mb="32px" style={{ gap: '16px' }}>
        <PortfolioVisibilityGroupButton
          selected={displayMode === 'all'}
          label={`Show All ${_.sum(Object.values(totalChartCounts))}`}
          onClick={() => onSetDisplayMode('all')}
        />
        <PortfolioVisibilityGroupButton
          selected={displayMode === 'borrowingBase'}
          label={`Borrowing Base ${totalChartCounts.borrowingBase}`}
          onClick={() => onSetDisplayMode('borrowingBase')}
        />
        <PortfolioVisibilityGroupButton
          selected={displayMode === 'eligibility'}
          label={`Eligibility ${totalChartCounts.eligibility}`}
          onClick={() => onSetDisplayMode('eligibility')}
        />
        <PortfolioVisibilityGroupButton
          selected={displayMode === 'concentration'}
          label={`Concentration ${totalChartCounts.concentration}`}
          onClick={() => onSetDisplayMode('concentration')}
        />
      </Flex>
      {_.map(chartGroupMap, (chartMaps, group) =>
        _.map(chartMaps, (chart) => {
          return (
            (displayMode === 'all' || displayMode === group) && (
              <PortfolioCoreBorrowingBaseChartTemplate
                creditFacilityId={creditFacilityId}
                chartTitle={chart.chartTitle}
                chartConfig={chart.config}
                key={`portfolio-borrowing-base-chart-${chart.chartTitle}`}
              />
            )
          );
        }),
      )}
      {(displayMode === 'all' || displayMode === 'eligibility') && (
        <PortfolioEligibilityKickoutsChart
          creditFacilityId={creditFacilityId}
          chartTitle="Eligibility Kickouts"
        />
      )}

      {(displayMode === 'all' || displayMode === 'concentration') && (
        <PortfolioExcessConcentrationChart
          creditFacilityId={creditFacilityId}
          chartTitle="Excess Concentration Amounts"
          concentrationLimitFormatMap={concentrationLimitFormatMap}
        />
      )}
      {(displayMode === 'all' || displayMode === 'concentration') &&
        concentrationLimitFormatMap.size > 0 && (
          <>
            <Heading as="h3" fontSize="24px" mb="24px">
              Concentration Limits
            </Heading>
            <HighlightBox backgroundColor={colors.millet[50]}>
              {Array.from(concentrationLimitFormatMap).map(
                ([concentrationLimitKey, valueType]) => (
                  <PortfolioConcentrationLimitsChartTemplate
                    creditFacilityId={creditFacilityId}
                    chartTitle={commonUtils.toTitleCase(concentrationLimitKey)}
                    chartConfig={generateConcentrationLimitChartConfig(
                      valueType === 'number' ? 'float' : valueType,
                    )}
                    concentrationLimitKey={concentrationLimitKey}
                    key={`portfolio-concentration-limit-chart-${concentrationLimitKey}`}
                  />
                ),
              )}
            </HighlightBox>
          </>
        )}
    </Box>
  );
};
